import { useEffect, useRef, useState } from "react"

// Number of bars the call-widget waveform renders.
export const MIC_LEVEL_BARS = 7

// Live microphone level for the call widget's "AI is
// listening" waveform (CON-163). While `active`, opens a
// lightweight analyser on the agent's mic and returns a
// `MIC_LEVEL_BARS`-element array of smoothed band levels
// (0..1), updated ~20fps.
//
// Returns `null` when inactive, when the browser has no
// `AudioContext` / `getUserMedia` (jsdom, headless test
// browser), or when the mic can't be opened — callers fall
// back to a static waveform. The hook never throws.
//
// A dedicated analyser stream (rather than reaching into the
// Voximplant SDK's call stream) keeps the visualizer
// decoupled and reuses the mic permission the call already
// obtained.
export function useMicLevel(active: boolean): number[] | null {
  const [levels, setLevels] = useState<number[] | null>(null)
  // Smoothing state kept across frames without re-rendering.
  const smoothed = useRef<number[]>(Array(MIC_LEVEL_BARS).fill(0))

  useEffect(() => {
    if (!active) {
      setLevels(null)
      return
    }

    const AudioCtx =
      typeof window !== "undefined"
        ? (window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext)
        : undefined

    // jsdom / unsupported-browser guard — no analysis, static
    // fallback, never throw.
    if (
      !AudioCtx ||
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      setLevels(null)
      return
    }

    let cancelled = false
    let raf = 0
    let ctx: AudioContext | null = null
    let stream: MediaStream | null = null
    let lastUpdate = 0
    smoothed.current = Array(MIC_LEVEL_BARS).fill(0)

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((mediaStream) => {
        if (cancelled) {
          mediaStream.getTracks().forEach((t) => t.stop())
          return
        }
        stream = mediaStream
        try {
          ctx = new AudioCtx()
          const source = ctx.createMediaStreamSource(mediaStream)
          const analyser = ctx.createAnalyser()
          analyser.fftSize = 64 // → 32 frequency bins
          analyser.smoothingTimeConstant = 0.7
          source.connect(analyser)
          const bins = new Uint8Array(analyser.frequencyBinCount)

          const tick = (now: number) => {
            if (cancelled) return
            raf = requestAnimationFrame(tick)
            // Throttle React updates to ~20fps; the bars are
            // tiny but we don't need 60fps re-renders.
            if (now - lastUpdate < 48) return
            lastUpdate = now

            analyser.getByteFrequencyData(bins)
            const next = new Array<number>(MIC_LEVEL_BARS)
            for (let i = 0; i < MIC_LEVEL_BARS; i++) {
              // Voice energy sits in the low bins; sample two
              // bins per bar from the bottom of the spectrum.
              const a = bins[i * 2] ?? 0
              const b = bins[i * 2 + 1] ?? 0
              // Gentle gain so quiet speech still moves the bars.
              const raw = Math.min(1, ((a + b) / 2 / 255) * 1.6)
              const prev = smoothed.current[i] ?? 0
              const value = prev * 0.6 + raw * 0.4
              smoothed.current[i] = value
              next[i] = value
            }
            setLevels(next)
          }
          raf = requestAnimationFrame(tick)
        } catch {
          // Setting up the analyser threw — release the mic and
          // the context we may have opened, then fall back.
          mediaStream.getTracks().forEach((t) => t.stop())
          stream = null
          void ctx?.close().catch(() => {})
          ctx = null
          if (!cancelled) setLevels(null)
        }
      })
      .catch(() => {
        if (!cancelled) setLevels(null)
      })

    return () => {
      cancelled = true
      if (raf) cancelAnimationFrame(raf)
      stream?.getTracks().forEach((t) => t.stop())
      void ctx?.close().catch(() => {})
    }
  }, [active])

  return levels
}
